"use client";

import { useState, useRef } from "react";
import { ChevronDown } from "lucide-react";
import CategoryDetailSection, {
  regionalTalentData,
  industryEcosystemData,
  lifelongEducationData,
  localIssuesData,
} from "./CategoryDetailSection";

/** 카테고리 타입 */
type CategoryType = "regional" | "industry" | "lifelong" | "local" | null;

/**
 * 하위 카테고리 카드 컴포넌트
 */
interface SubCategoryCardProps {
  title: string;
  items?: string[];
}

function SubCategoryCard({ title, items }: SubCategoryCardProps) {
  return (
    <div className="flex items-start">
      {/* 연결선 - 가로선 */}
      <div className="flex items-center" style={{ marginTop: "16px" }}>
        <div
          className="bg-gray-400 rounded-full"
          style={{ width: "6px", height: "6px" }}
        />
        <div className="bg-gray-400" style={{ width: "16px", height: "2px" }} />
      </div>
      <div
        className="rounded-lg border border-[#fff] bg-white overflow-hidden shadow-md"
        style={{ width: "272px" }}
      >
        {/* 타이틀 */}
        <div className="p-2" style={{ backgroundColor: "#ffff" }}>
          <div className="w-full py-2 px-2 text-center text-[#4B2BAD] font-bold text-sm bg-[#F4F4FF] rounded-lg border border-[#9184DA]">
            {title}
          </div>
        </div>
        {/* 아이템 리스트 */}
        {items && items.length > 0 && (
          <ul className="p-3 space-y-1">
            {items.map((item, idx) => (
              <li key={idx} className="flex items-start text-sm text-gray-700">
                <span className="mr-2 text-gray-400">•</span>
                {item}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * 단일 박스형 하위 항목 (리스트 없이 타이틀만)
 */
interface SimpleBoxProps {
  title: string;
}

function SimpleBox({ title }: SimpleBoxProps) {
  return (
    <div className="flex items-center">
      {/* 연결선 - 가로선 */}
      <div className="flex items-center">
        <div
          className="bg-gray-400 rounded-full"
          style={{ width: "6px", height: "6px" }}
        />
        <div className="bg-gray-400" style={{ width: "16px", height: "2px" }} />
      </div>
      <div
        className="rounded-lg border border-[#fff] p-2 shadow-md"
        style={{ width: "272px", backgroundColor: "#fff" }}
      >
        <div className="w-full py-2 px-4 text-center text-[#4B2BAD] font-bold text-sm bg-[#F4F4FF] rounded-lg border border-[#9184DA]">
          {title}
        </div>
      </div>
    </div>
  );
}

/**
 * 하위 항목들을 세로선으로 연결하는 컨테이너
 */
interface SubItemsContainerProps {
  children: React.ReactNode;
  /** 세로선 높이 (px) - 카테고리부터 마지막 항목 타이틀 중앙까지 */
  lineHeight: number;
}

function SubItemsContainer({ children, lineHeight }: SubItemsContainerProps) {
  return (
    <div className="relative">
      {/* 세로 연결선 - 카테고리에서 이어져서 마지막 항목 타이틀 중앙까지 */}
      <div
        className="absolute bg-gray-400"
        style={{
          width: "2px",
          height: `${lineHeight}px`,
          top: "0",
          left: "2px",
        }}
      />
      <div className="space-y-3 pt-4">{children}</div>
    </div>
  );
}

/**
 * 모바일용 아코디언 아이템 컴포넌트
 */
interface MobileAccordionItemProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  color?: string;
}

function MobileAccordionItem({
  title,
  isOpen,
  onToggle,
  children,
  color = "#3436B0",
}: MobileAccordionItemProps) {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-white font-bold text-sm"
        style={{ backgroundColor: color }}
      >
        <span>{title}</span>
        <ChevronDown
          className={`w-5 h-5 transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="p-3 space-y-3">{children}</div>
      </div>
    </div>
  );
}

/**
 * 모바일용 하위 카테고리 카드
 */
interface MobileSubCategoryCardProps {
  title: string;
  items?: string[];
}

function MobileSubCategoryCard({ title, items }: MobileSubCategoryCardProps) {
  return (
    <div className="border border-[#E8E8FF] rounded-lg overflow-hidden">
      <div className="py-2 px-3 bg-[#F4F4FF] border-b border-[#9184DA]">
        <span className="text-[#4B2BAD] font-bold text-sm">{title}</span>
      </div>
      {items && items.length > 0 && (
        <ul className="p-3 space-y-1 bg-white">
          {items.map((item, idx) => (
            <li key={idx} className="flex items-start text-sm text-gray-700">
              <span className="mr-2 text-gray-400">•</span>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * 모바일용 목표/세부 프로그램 섹션
 */
interface MobileDetailSectionProps {
  goalTitle: string;
  goals: string[];
  programTitle: string;
  programs: string[];
}

function MobileDetailSection({
  goalTitle,
  goals,
  programTitle,
  programs,
}: MobileDetailSectionProps) {
  return (
    <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
      {/* 목표 섹션 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="py-2 px-3 bg-gray-50 border-b border-gray-200">
          <span className="text-gray-800 font-bold text-sm">{goalTitle}</span>
        </div>
        <div className="p-3 space-y-2 bg-white">
          {goals.map((goal, idx) => (
            <div
              key={idx}
              className="py-2 px-3 bg-gray-50 rounded text-sm text-gray-700"
            >
              {goal}
            </div>
          ))}
        </div>
      </div>
      {/* 세부 프로그램 섹션 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="py-2 px-3 bg-[#5A5F6E]">
          <span className="text-white font-bold text-sm">{programTitle}</span>
        </div>
        <div className="p-3 space-y-2 bg-white">
          {programs.map((program, idx) => (
            <div
              key={idx}
              className="py-2 px-3 bg-gray-50 rounded text-sm text-gray-700 border border-gray-200"
            >
              {program}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * 모바일용 RISE 추진과제 컴포넌트
 */
function MobileRiseHierarchy() {
  const [openCategory, setOpenCategory] = useState<CategoryType>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const toggleCategory = (category: CategoryType) => {
    const isOpening = openCategory !== category;
    setOpenCategory(openCategory === category ? null : category);

    // 새 카테고리를 열 때 스크롤
    if (isOpening && category) {
      setTimeout(() => {
        categoryRefs.current[category]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  };

  return (
    <div
      className="w-full py-8 px-4"
      style={{
        backgroundImage: "url('/bg-image.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* 메인 타이틀 */}
      <div className="flex justify-center mb-6">
        <div className="w-full max-w-sm py-3 px-4 bg-[#5A5F6E] text-white font-bold text-base text-center rounded-lg shadow-md">
          RISE추진과제 및 세부사업
        </div>
      </div>

      {/* 아코디언 목록 */}
      <div className="space-y-3 max-w-sm mx-auto">
        {/* 1. 지역정주형인재양성 */}
        <div ref={(el) => { categoryRefs.current["regional"] = el; }} style={{ scrollMarginTop: "20px" }}>
          <MobileAccordionItem
            title="지역정주형인재양성"
            isOpen={openCategory === "regional"}
            onToggle={() => toggleCategory("regional")}
          >
            <MobileSubCategoryCard
              title="핵심인재양성"
              items={["공유대학", "이차전지/반도체/바이오"]}
            />
            <MobileSubCategoryCard
              title="시군특화형"
              items={[
                "AI모빌리티",
                "AI셀뱅크",
                "첨단농업(Agri-Life)",
                "충주형 · 증평형 뿌리",
                "사이버보안",
              ]}
            />
            <MobileSubCategoryCard
              title="클러스터"
              items={["오송첨단", "순환경제", "보건의료"]}
            />
            <MobileDetailSection
              goalTitle="목표 : 지역 전략산업 경쟁력 강화"
              goals={[
                "지역 전략산업 경쟁력 강화",
                "산업별 전문가 참여 실무 중심",
                "첨단 산업기반 산학협력 강화",
                "융복합&글로벌 인재양성",
              ]}
              programTitle="세부 운영 프로그램"
              programs={[
                "취업패키지",
                "대학원 산학연계 인재양성 프로그램",
                "표준현장실습",
                "기업맞춤형트랙",
                "캡스톤 디자인",
                "프리칼리지",
              ]}
            />
          </MobileAccordionItem>
        </div>

        {/* 2. 지산학연협력생태계구축 */}
        <div ref={(el) => { categoryRefs.current["industry"] = el; }} style={{ scrollMarginTop: "20px" }}>
          <MobileAccordionItem
            title="지산학연협력생태계구축"
            isOpen={openCategory === "industry"}
            onToggle={() => toggleCategory("industry")}
          >
            <MobileSubCategoryCard
              title="공동연구센터"
              items={[
                "첨단바이오",
                "이차전지",
                "반도체",
                "AI모빌리티",
                "첨단로봇",
                "AX기업혁신",
              ]}
            />
            <MobileSubCategoryCard title="창업지원센터" />
            <MobileSubCategoryCard title="방사광가속기" />
            <MobileDetailSection
              goalTitle="목표 : 지역 산학연 협력 강화"
              goals={[
                "산학연 공동연구 활성화",
                "기술이전 및 사업화 촉진",
                "지역 산업 경쟁력 제고",
              ]}
              programTitle="세부 운영 프로그램"
              programs={[
                "공동연구개발",
                "기술이전 프로그램",
                "창업 인큐베이팅",
                "산학협력 네트워크",
              ]}
            />
          </MobileAccordionItem>
        </div>

        {/* 3. 평생교육체제확대 */}
        <div ref={(el) => { categoryRefs.current["lifelong"] = el; }} style={{ scrollMarginTop: "20px" }}>
          <MobileAccordionItem
            title="평생교육체제확대"
            isOpen={openCategory === "lifelong"}
            onToggle={() => toggleCategory("lifelong")}
          >
            <MobileSubCategoryCard title="에이스브릿지" />
            <MobileSubCategoryCard title="전문간호" />
            <MobileDetailSection
              goalTitle="목표 : 평생학습 기회 확대"
              goals={[
                "성인학습자 맞춤형 교육",
                "직무능력 향상 지원",
                "학력 취득 기회 제공",
              ]}
              programTitle="세부 운영 프로그램"
              programs={[
                "성인학습자 특별전형",
                "선취업 후학습 과정",
                "직무역량 강화 프로그램",
              ]}
            />
          </MobileAccordionItem>
        </div>

        {/* 4. 지역현안해결 */}
        <div ref={(el) => { categoryRefs.current["local"] = el; }} style={{ scrollMarginTop: "20px" }}>
          <MobileAccordionItem
            title="지역현안해결"
            isOpen={openCategory === "local"}
            onToggle={() => toggleCategory("local")}
          >
            <MobileSubCategoryCard title="K-유학생" />
            <MobileSubCategoryCard title="K-유학생 (기술창업)" />
            <MobileSubCategoryCard title="생활연구소" />
            <MobileSubCategoryCard title="늘봄" />
            <MobileDetailSection
              goalTitle="목표 : 지역사회 문제 해결"
              goals={[
                "외국인 유학생 유치 및 정착 지원",
                "지역 생활 밀착형 연구",
                "지역사회 교육 지원",
              ]}
              programTitle="세부 운영 프로그램"
              programs={[
                "유학생 한국어 교육",
                "기술창업 멘토링",
                "생활밀착형 연구사업",
                "돌봄 서비스 연계",
              ]}
            />
          </MobileAccordionItem>
        </div>
      </div>
    </div>
  );
}

/**
 * 데스크탑용 RISE 추진과제 컴포넌트
 */
function DesktopRiseHierarchy() {
  const [selectedCategory, setSelectedCategory] = useState<CategoryType>(null);
  const detailSectionRef = useRef<HTMLDivElement>(null);

  const handleCategoryClick = (category: CategoryType) => {
    const isOpening = selectedCategory !== category;
    setSelectedCategory(selectedCategory === category ? null : category);

    // 새 카테고리를 열 때 스크롤
    if (isOpening) {
      // 상태 업데이트 후 DOM이 렌더링되면 스크롤
      setTimeout(() => {
        detailSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  };

  const getCategoryData = () => {
    switch (selectedCategory) {
      case "regional":
        return regionalTalentData;
      case "industry":
        return industryEcosystemData;
      case "lifelong":
        return lifelongEducationData;
      case "local":
        return localIssuesData;
      default:
        return null;
    }
  };

  return (
    <div
      className={`relative w-full py-32 ${
        selectedCategory ? "min-h-[1400px]" : "min-h-[1100px]"
      }`}
      style={{
        backgroundImage: "url('/bg-image.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* 컨테이너 - 중앙 정렬용 */}
      <div className="relative mx-auto" style={{ width: "1104px" }}>
        {/* 메인 타이틀 박스 - 562x72 */}
        <div className="flex justify-center">
          <div
            className="flex items-center justify-center bg-[#5A5F6E] text-white font-bold text-xl rounded-lg border-2 border-[#5A5F6E] shadow-md"
            style={{
              width: "562px",
              height: "72px",
            }}
          >
            RISE추진과제 및 세부사업
          </div>
        </div>

        {/* 세로선 - 타이틀 박스 중앙에서 아래로 32px */}
        <div className="flex justify-center">
          <div
            className="bg-gray-400"
            style={{
              width: "2px",
              height: "32px",
            }}
          />
        </div>

        {/* 가로선 - 1104x2, 4개의 변곡점(양 끝 + 중간 2개 = 4등분) */}
        <div className="relative" style={{ width: "1104px", height: "2px" }}>
          {/* 가로선 본체 */}
          <div
            className="absolute top-0 left-0 bg-gray-400"
            style={{
              width: "1104px",
              height: "2px",
            }}
          />

          {/* 변곡점 1 - 0% (좌측 끝) */}
          <div
            className="absolute bg-gray-500 rounded-full"
            style={{
              width: "8px",
              height: "8px",
              top: "-3px",
              left: "0px",
              transform: "translateX(-50%)",
            }}
          />

          {/* 변곡점 2 - 33.33% */}
          <div
            className="absolute bg-gray-500 rounded-full"
            style={{
              width: "8px",
              height: "8px",
              top: "-3px",
              left: "368px", // 1104 / 3
              transform: "translateX(-50%)",
            }}
          />

          {/* 변곡점 3 - 66.66% */}
          <div
            className="absolute bg-gray-500 rounded-full"
            style={{
              width: "8px",
              height: "8px",
              top: "-3px",
              left: "736px", // 1104 * 2 / 3
              transform: "translateX(-50%)",
            }}
          />

          {/* 변곡점 4 - 100% (우측 끝) */}
          <div
            className="absolute bg-gray-500 rounded-full"
            style={{
              width: "8px",
              height: "8px",
              top: "-3px",
              left: "1104px",
              transform: "translateX(-50%)",
            }}
          />
        </div>

        {/* 4개의 카테고리 박스 - 각 변곡점에서 세로선 내려오고 박스 배치 */}
        <div className="relative" style={{ width: "1104px" }}>
          {/* 카테고리 1 - 지역정주형인재양성 (좌측 끝) */}
          <div
            className="absolute flex flex-col items-center"
            style={{ left: "0px", transform: "translateX(-50%)" }}
          >
            <div
              className="bg-gray-400"
              style={{ width: "2px", height: "32px" }}
            />
            <button
              onClick={() => handleCategoryClick("regional")}
              className={`flex items-center justify-center text-white font-bold text-base rounded-lg shadow-md cursor-pointer transition-all duration-200 hover:scale-105 ${
                selectedCategory === "regional"
                  ? "bg-[#E91E8C] ring-2 ring-[#E91E8C] ring-offset-2"
                  : "bg-[#3436B0] hover:bg-[#4547C0]"
              }`}
              style={{ width: "320px", height: "60px" }}
            >
              지역정주형인재양성
            </button>
            {/* 하위 항목들 */}
            <SubItemsContainer lineHeight={376}>
              <SubCategoryCard
                title="핵심인재양성"
                items={["공유대학", "이차전지/반도체/바이오"]}
              />
              <SubCategoryCard
                title="시군특화형"
                items={[
                  "AI모빌리티",
                  "AI셀뱅크",
                  "첨단농업(Agri-Life)",
                  "충주형 · 증평형 뿌리",
                  "사이버보안",
                ]}
              />
              <SubCategoryCard
                title="클러스터"
                items={["오송첨단", "순환경제", "보건의료"]}
              />
            </SubItemsContainer>
          </div>

          {/* 카테고리 2 - 지산학연협력생태계구축 (1/3 지점) */}
          <div
            className="absolute flex flex-col items-center"
            style={{ left: "368px", transform: "translateX(-50%)" }}
          >
            <div
              className="bg-gray-400"
              style={{ width: "2px", height: "32px" }}
            />
            <button
              onClick={() => handleCategoryClick("industry")}
              className={`flex items-center justify-center text-white font-bold text-base rounded-lg shadow-md cursor-pointer transition-all duration-200 hover:scale-105 ${
                selectedCategory === "industry"
                  ? "bg-[#E91E8C] ring-2 ring-[#E91E8C] ring-offset-2"
                  : "bg-[#3436B0] hover:bg-[#4547C0]"
              }`}
              style={{ width: "320px", height: "60px" }}
            >
              지산학연협력생태계구축
            </button>
            {/* 하위 항목들 */}
            <SubItemsContainer lineHeight={342}>
              <SubCategoryCard
                title="공동연구센터"
                items={[
                  "첨단바이오",
                  "이차전지",
                  "반도체",
                  "AI모빌리티",
                  "첨단로봇",
                  "AX기업혁신",
                ]}
              />
              <SimpleBox title="창업지원센터" />
              <SimpleBox title="방사광가속기" />
            </SubItemsContainer>
          </div>

          {/* 카테고리 3 - 평생교육체제확대 (2/3 지점) */}
          <div
            className="absolute flex flex-col items-center"
            style={{ left: "736px", transform: "translateX(-50%)" }}
          >
            <div
              className="bg-gray-400"
              style={{ width: "2px", height: "32px" }}
            />
            <button
              onClick={() => handleCategoryClick("lifelong")}
              className={`flex items-center justify-center text-white font-bold text-base rounded-lg shadow-md cursor-pointer transition-all duration-200 hover:scale-105 ${
                selectedCategory === "lifelong"
                  ? "bg-[#E91E8C] ring-2 ring-[#E91E8C] ring-offset-2"
                  : "bg-[#3436B0] hover:bg-[#4547C0]"
              }`}
              style={{ width: "320px", height: "60px" }}
            >
              평생교육체제확대
            </button>
            {/* 하위 항목들 */}
            <SubItemsContainer lineHeight={110}>
              <SimpleBox title="에이스브릿지" />
              <SimpleBox title="전문간호" />
            </SubItemsContainer>
          </div>

          {/* 카테고리 4 - 지역현안해결 (우측 끝) */}
          <div
            className="absolute flex flex-col items-center"
            style={{ left: "1104px", transform: "translateX(-50%)" }}
          >
            <div
              className="bg-gray-400"
              style={{ width: "2px", height: "32px" }}
            />
            <button
              onClick={() => handleCategoryClick("local")}
              className={`flex items-center justify-center text-white font-bold text-base rounded-lg shadow-md cursor-pointer transition-all duration-200 hover:scale-105 ${
                selectedCategory === "local"
                  ? "bg-[#E91E8C] ring-2 ring-[#E91E8C] ring-offset-2"
                  : "bg-[#3436B0] hover:bg-[#4547C0]"
              }`}
              style={{ width: "320px", height: "60px" }}
            >
              지역현안해결
            </button>
            {/* 하위 항목들 */}
            <SubItemsContainer lineHeight={246}>
              <SimpleBox title="K-유학생" />
              <SimpleBox title="K-유학생 (기술창업)" />
              <SimpleBox title="생활연구소" />
              <SimpleBox title="늘봄" />
            </SubItemsContainer>
          </div>
        </div>
      </div>

      {/* 카테고리 상세 섹션 */}
      {selectedCategory && getCategoryData() ? (
        <div ref={detailSectionRef} style={{ marginTop: "670px", scrollMarginTop: "80px" }}>
          {/* 가로선 */}
          <div className="flex justify-center">
            <div
              className="bg-[#E8E8E8]"
              style={{ width: "1204px", height: "2px" }}
            />
          </div>
          <CategoryDetailSection data={getCategoryData()!} />
        </div>
      ) : (
        <div
          className="flex justify-center items-center"
          style={{ marginTop: "670px" }}
        >
          <p className="text-gray-500 text-lg">
            위의 RISE 추진사업을 클릭하여 상세 정보를 확인하세요.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * RISE 추진과제 및 세부사업 계층 구조 컴포넌트 (반응형)
 */
export default function RiseHierarchy() {
  return (
    <>
      {/* 모바일 뷰 (1280px 미만) */}
      <div className="block xl:hidden">
        <MobileRiseHierarchy />
      </div>
      {/* 데스크탑 뷰 (1280px 이상) */}
      <div className="hidden xl:block">
        <DesktopRiseHierarchy />
      </div>
    </>
  );
}
